(function () {
    /********************* Setup *********************/

        // save the section id from the script tag and find the section
    const sectionID = document.currentScript.dataset.id;
    const section = document.querySelector(`[id="${sectionID}"].productV10`);

    // exit if section is not found
    if (!section) {
        console.warn(`Product section with ID ${sectionID} not found.`);
        return;
    }

    // product state, re-read from the markup every time the section is re-rendered
    let product = null;
    let activeVariant = null;
    let preOrder = {};

    // swiper instances, destroyed and rebuilt whenever their markup is replaced
    let mainSwiper = null;
    let mainThumbsSwiper = null;
    let variantSwiper = null;
    let variantThumbsSwiper = null;

    // json payloads liquid embeds at the top of the section
    const JSON_REGIONS = [".productV10JSON", ".productV10VariantJSON", ".productV10PreOrderJSON"];

    // product driven regions replaced when the shopper picks another style. the discount blocks are
    // left alone because a standalone section render has no blocks, and the add to cart form is left
    // alone so the dynamic checkout button keeps working
    const PRODUCT_REGIONS = [
        ".productV10Visual",
        ".productV10Review",
        ".productV10Title",
        ".productV10Price",
        ".productV10ShortDescription",
        ".productV10OptionsWrapper",
        ".productV10Description"
    ];

    /********************* Section Render *********************/

    // ask shopify to render this section on its own against a product url, the only context where
    // liquid resolves a different product and where ?variant= picks the variant specific carousel
    async function fetchSection(path, variantID) {
        const url = new URL(path, window.location.origin);
        url.searchParams.set("sections", "main-product-v10");
        if (variantID) {
            url.searchParams.set("variant", variantID);
        }

        const response = await fetch(url);
        if (!response.ok) {
            return null;
        }

        // parse the returned markup so the regions can be pulled out of it
        const data = await response.json();
        const holder = document.createElement("div");
        holder.innerHTML = data["main-product-v10"] || "";
        return holder.querySelector(".productV10");
    }

    // swap the product driven markup for another style without leaving the page
    async function selectStyle(styleItem) {
        if (styleItem.classList.contains("active")) {
            return;
        }

        // the round trip is visible, so show the shopper their click landed
        styleItem.classList.add("loading");
        const rendered = await fetchSection(styleItem.dataset.url);
        styleItem.classList.remove("loading");

        if (!rendered) {
            return;
        }

        // swiper holds listeners on markup that is about to be thrown away
        destroySwipers(mainSwiper, mainThumbsSwiper, variantSwiper, variantThumbsSwiper);

        // the json tags carry the new product, variant and pre-order data
        JSON_REGIONS.forEach(selector => {
            section.querySelector(selector).textContent = rendered.querySelector(selector).textContent;
        });

        PRODUCT_REGIONS.forEach(selector => {
            const target = section.querySelector(selector);
            const source = rendered.querySelector(selector);
            if (!target || !source) {
                return;
            }
            target.innerHTML = source.innerHTML;
        });

        // later gallery renders have to go to the new product's url
        section.dataset.url = rendered.dataset.url;

        // start on the full product gallery, the same as a fresh page load
        section.querySelector(".productV10Visual").classList.add("mainVisual");

        setQuantity(1);
        initSection();
    }

    /********************* Galleries *********************/

    // destroy instances whose markup is being replaced so swiper drops its listeners
    function destroySwipers(...instances) {
        instances.forEach(instance => instance?.destroy(true, true));
    }

    // mark the thumbnail matching the slide now on screen
    function syncThumbs(scopeSelector, index) {
        const scope = section.querySelector(scopeSelector);
        scope.querySelectorAll(".productV10VisualThumbsSlide.active").forEach(el => el.classList.remove("active"));
        scope.querySelectorAll(`.productV10VisualThumbsSlide[data-index="${index}"]`).forEach(el => el.classList.add("active"));
    }

    // stop any gallery video so a slide change never leaves audio running
    function pauseGalleryVideos() {
        section.querySelectorAll(".productV10VisualSwiperSlideVideo").forEach(video => video.pause());
    }

    // the main gallery holds every product image and is always in the markup
    function initMainSwipers() {
        const swiperElement = section.querySelector(".productV10VisualMainSwiper");
        mainSwiper = new Swiper(swiperElement, {
            slidesPerView: 1,
            spaceBetween: 20,
            loop: true,
            zoom: {
                maxRatio: 2,
                toggle: false
            },
            navigation: {
                nextEl: swiperElement.querySelector(".swiper-button-next"),
                prevEl: swiperElement.querySelector(".swiper-button-prev"),
            },
        });

        mainSwiper.on("slideChange", function () {
            syncThumbs(".productV10VisualMain", mainSwiper.realIndex + 1);
            pauseGalleryVideos();
        });

        // the thumbnails only scroll on mobile, where they are wrapped in their own swiper
        const thumbsElement = section.querySelector(".productV10VisualMainSwiperThumbs.mobileOnly");
        mainThumbsSwiper = new Swiper(thumbsElement.querySelector(".swiper"), {
            slidesPerView: "auto",
            navigation: {
                nextEl: thumbsElement.querySelector(".swiper-button-next"),
                prevEl: thumbsElement.querySelector(".swiper-button-prev"),
            },
        });
    }

    // the variant gallery only exists when a variant carries a pdp_variant_carousel metafield
    function initVariantSwipers() {
        const swiperElement = section.querySelector(".productV10VisualSwiper");
        if (!swiperElement) {
            variantSwiper = null;
            variantThumbsSwiper = null;
            return;
        }

        variantSwiper = new Swiper(swiperElement, {
            slidesPerView: 1,
            spaceBetween: 20,
            loop: true,
            zoom: {
                maxRatio: 2,
                toggle: false
            },
            navigation: {
                nextEl: swiperElement.querySelector(".swiper-button-next"),
                prevEl: swiperElement.querySelector(".swiper-button-prev"),
            },
        });

        variantSwiper.on("slideChange", function () {
            syncThumbs(".productV10VisualVariant", variantSwiper.realIndex + 1);
            pauseGalleryVideos();
        });

        const thumbsElement = section.querySelector(".productV10VisualThumbs.mobileOnly");
        variantThumbsSwiper = new Swiper(thumbsElement.querySelector(".swiper"), {
            slidesPerView: "auto",
            navigation: {
                nextEl: thumbsElement.querySelector(".swiper-button-next"),
                prevEl: thumbsElement.querySelector(".swiper-button-prev"),
            },
        });
    }

    // move the gallery the thumbnail belongs to onto that slide
    function selectThumb(thumb) {
        const isMain = !!thumb.closest(".productV10VisualMain");
        syncThumbs(isMain ? ".productV10VisualMain" : ".productV10VisualVariant", thumb.dataset.index);
        const swiper = isMain ? mainSwiper : variantSwiper;
        swiper?.slideToLoop(thumb.dataset.index - 1);
    }

    // reveal and play the video sitting on a gallery slide
    function playGalleryVideo(slide) {
        const video = slide.querySelector(".productV10VisualSwiperSlideVideo");
        if (!video) {
            return;
        }

        // the play overlay is only there until the first click
        slide.querySelector(".productV10VisualSwiperSlidePlay")?.remove();
        video.controls = true;
        video.play();
    }

    // liquid decides which images a variant shows, so re-render the gallery against the product url
    async function updateVariantGallery() {
        // products without a variant carousel just move the main gallery to the variant image
        if (!section.querySelector(".productV10VisualSwiper")) {
            if (activeVariant.featured_image) {
                mainSwiper.slideToLoop(activeVariant.featured_image.position - 1);
            }
            return;
        }

        const rendered = await fetchSection(section.dataset.url, activeVariant.id);
        const gallery = rendered?.querySelector(".productV10VisualVariant");
        if (!gallery || !gallery.innerHTML.trim()) {
            return;
        }

        destroySwipers(variantSwiper, variantThumbsSwiper);
        section.querySelector(".productV10VisualVariant").innerHTML = gallery.innerHTML;
        initVariantSwipers();

        // show the variant gallery now that it matches the selection
        section.querySelector(".productV10Visual").classList.remove("mainVisual");
    }

    /********************* Quantity *********************/

    // write the quantity into the main form
    function setQuantity(value) {
        const quantity = Math.max(1, parseInt(value, 10) || 1);
        section.querySelector(".productV10FormQuantityInput").value = quantity;
        section.querySelector(".productV10FormQuantityNumberDisplay").textContent = String(quantity);
    }

    // step the quantity from the plus/minus buttons
    function stepQuantity(button) {
        let quantity = Number(section.querySelector(".productV10FormQuantityInput").value);
        if (button.classList.contains("productV10FormQuantityButtonLess")) {
            quantity--;
        } else {
            quantity++;
        }
        setQuantity(quantity);
    }

    /********************* Variants *********************/

    // find the variant matching the checked option inputs
    function findActiveVariant() {
        const options = [];
        section.querySelectorAll(".productV10OptionItemValuesLabelInput:checked").forEach(element => {
            options.push(element.value);
        });

        // default variant products have no inputs to read
        if (options.length === 0) {
            return activeVariant;
        }

        return product.variants.find(variant => JSON.stringify(variant.options) === JSON.stringify(options));
    }

    // push the active variant into the add to cart form and the widgets that read it
    function syncForm() {
        const atc = section.querySelector(".productV10FormATC");
        section.querySelector(".productV10FormVariant").value = activeVariant.id;

        if (activeVariant.available) {
            atc.classList.remove("disabled");
            atc.textContent = "Add To Cart";
        } else {
            atc.classList.add("disabled");
            atc.textContent = "Sold Out";
        }

        preOrderUpdate(activeVariant.id);
        updateAfterpay();
    }

    // rewrite the price block for the active variant
    function syncPrice() {
        const priceReal = section.querySelector(".productV10PriceReal");
        const priceCompare = section.querySelector(".productV10PriceCompare");
        const priceSavings = section.querySelector(".productV10PriceSavings");

        // update intelligems id
        section.querySelectorAll('.productV10PriceReal, .productV10PriceCompare, .productV10PriceSavings').forEach(el => {
            el.setAttribute('data-variant-id', activeVariant.id);
        });

        priceReal.textContent = price(activeVariant.price);

        // check for global sale
        if (section.querySelector('.productGlobalSales')) {
            const salePrice = price(activeVariant.price - ((Number(section.querySelector('.productGlobalSales').dataset.globalSalePercentage) * activeVariant.price) / 100));
            section.querySelectorAll('.productGlobalSales').forEach(el => el.textContent = salePrice);
        }

        if (activeVariant.compare_at_price > activeVariant.price) {
            // a global sale styles the price as struck through instead
            priceReal.classList.toggle("sale", !section.querySelector('.productGlobalSales'));
            priceCompare.textContent = price(activeVariant.compare_at_price);
            priceSavings.textContent = `Save ${Math.round((activeVariant.compare_at_price - activeVariant.price) * 100 / activeVariant.compare_at_price)}%`;
            return;
        }

        priceReal.classList.remove("sale");
        priceCompare.textContent = "";
        priceSavings.textContent = "";
    }

    // colour options print the chosen swatch name next to the label
    function syncColourLabel() {
        const label = section.querySelector(".productV10OptionItemLabelTitle");
        const checked = section.querySelector(".productV10OptionItemValues.color .productV10OptionItemValuesLabelInput:checked");
        if (label && checked) {
            label.textContent = checked.value;
        }
    }

    // pre-order variants swap the dynamic checkout button for a message
    function preOrderUpdate(id) {
        const message = preOrder[id];
        const metaInput = document.querySelector('.productV10FormMetaInput');
        // remove metafield input if it exists
        if (!message) {
            section.querySelector(".productV10PreOrder").classList.remove('active');
            section.querySelector(".productV10PreOrderMessage").textContent = "";
            if (metaInput) {
                document.querySelector('.productV10FormMetaInput').remove();
            }
            return;
        }
        section.querySelector(".productV10PreOrder").classList.add('active');
        section.querySelector(".productV10PreOrderMessage").textContent = message;
        section.querySelector(".productV10FormATC").textContent = "Pre-Order Now";
        // create an input element and add to form if none exists
        if (metaInput) {
            metaInput.setAttribute("value", message);
        } else {
            const propertyInput = document.createElement("input");
            propertyInput.setAttribute("name", "properties[Pre-Order]");
            propertyInput.setAttribute("type", "hidden");
            propertyInput.setAttribute("value", message);
            propertyInput.classList.add("productV10FormMetaInput");
            document.querySelector('.productV10Form').appendChild(propertyInput);
        }
    }

    // afterpay renders its own widget and needs the variant handed to it
    function updateAfterpay() {
        try {
            afterpay_current_variant = activeVariant;

            const afterpayElement = document.querySelector(`[data-mpid="muscle-mat.myshopify.com"]`);
            if (afterpayElement) {
                afterpayElement.dataset.amount = activeVariant.price / 100;
            }
        } catch (e) {
            console.error("Error updating afterpay pricing");
        }
    }

    // recalculate the active variant from the inputs and refresh everything that depends on it
    async function updateProduct() {
        activeVariant = findActiveVariant();

        // no variant matches the chosen options, so move onto a size that exists
        if (!activeVariant) {
            if (!nextAvailableSize()) {
                const atc = section.querySelector(".productV10FormATC");
                atc.classList.add("disabled");
                atc.textContent = "Not Available";
            }
            return;
        }

        syncForm();
        syncPrice();
        syncColourLabel();

        // sold out on a multi option product usually means another size is available
        if (!activeVariant.available && product.options.length > 1 && nextAvailableSize()) {
            return;
        }

        try {
            await updateVariantGallery();
        } catch (e) {
            console.warn("Variant image unable to update");
            console.error(e);
        }

        // keep the selected variant in the url so it survives a refresh
        const url = new URL(window.location.href);
        url.searchParams.set("variant", activeVariant.id);
        window.history.replaceState({}, "", url);
    }

    // disable size options that are not available for the selected colour
    function updateUnavailableOptions() {
        // only applies to products built as colour then size
        if (product.options.length !== 2) {
            return;
        }
        if (product.options[0] !== "Colour" && product.options[0] !== "Color") {
            return;
        }
        if (product.options[1] !== "Size") {
            return;
        }

        // collect options from inputs
        const options = [];
        section.querySelectorAll(".productV10OptionItemValuesLabelInput:checked").forEach(element => {
            options.push(element.value);
        });

        const selectedColour = options[0];
        const selectedSize = options[1];

        section.querySelectorAll(`.productV10OptionItem[data-value="Size"] .productV10OptionItemValuesLabelInput`).forEach(sizeInput => {
            // never disable the size that is already selected
            if (sizeInput.value === selectedSize) {
                return;
            }

            // check whether a variant exists for the selected colour at this size
            const testVariant = product.variants.find(variant => {
                return JSON.stringify(variant.options) === JSON.stringify([selectedColour, sizeInput.value]);
            });

            sizeInput.disabled = !testVariant || !testVariant.available;
        });
    }

    // select the first size that is still in stock
    function nextAvailableSize() {
        // behaviour is disruptive on products that only have a size option
        if (product.options.length !== 2) {
            return false;
        }

        const sizeInputs = section.querySelectorAll(`.productV10OptionItem[data-value="Size"] .productV10OptionItemValuesLabelInput`);

        for (const input of sizeInputs) {
            if (!input.disabled && !input.checked) {
                input.checked = true;
                input.dispatchEvent(new Event("input", {bubbles: true}));
                return true;
            }
        }

        // none found
        return false;
    }

    /********************* Add To Cart *********************/

    async function submitForm(form) {
        const button = form.querySelector(".productV10FormATC");
        button.classList.add("loading");

        const formData = new FormData(form);

        // pre-order variants carry their message through as a line item property
        if (preOrder[activeVariant.id]) {
            formData.delete("properties");
            formData.set("properties[Pre-Order]", preOrder[activeVariant.id]);
        }

        const addedProduct = await addProduct(formData);

        // klaviyo tracking
        try {
            _learnq.push(["track", "Added to Cart", addedProduct]);
        } catch (error) {
            console.error("Klaviyo tracking not found");
        }

        button.classList.remove("loading");
        await openCart(addedProduct);
    }

    /********************* Events *********************/

    // one delegated click handler for the whole section so re-rendered markup never needs re-binding
    section.addEventListener("click", function (e) {
        const thumb = e.target.closest(".productV10VisualThumbsSlide");
        if (thumb) {
            selectThumb(thumb);
            return;
        }

        const styleItem = e.target.closest(".productV10StyleItem");
        if (styleItem) {
            selectStyle(styleItem).catch(error => console.error(error));
            return;
        }

        // the view all button drops back to the full product gallery
        if (e.target.closest(".productV10Gallery")) {
            section.querySelector(".productV10Visual").classList.add("mainVisual");
            return;
        }

        const quantityButton = e.target.closest(".productV10FormQuantityButton");
        if (quantityButton) {
            stepQuantity(quantityButton);
            return;
        }

        // re-clicking the selected option brings the variant gallery back into view
        if (e.target.closest(".productV10OptionItemValuesLabel") && section.querySelector(".productV10VisualSwiper")) {
            section.querySelector(".productV10Visual").classList.remove("mainVisual");
        }

        // zoom is a single click, so it is handled here rather than by swiper
        const zoomTarget = e.target.closest(".swiper-zoom-container");
        if (zoomTarget) {
            const swiper = zoomTarget.closest(".productV10VisualMain") ? mainSwiper : variantSwiper;
            swiper?.zoom.toggle();
        }

        const gallerySlide = e.target.closest(".productV10VisualSwiperSlide");
        if (gallerySlide) {
            playGalleryVideo(gallerySlide);
        }
    });

    // option radios are the only inputs that change the variant
    section.addEventListener("input", function (e) {
        const optionInput = e.target.closest(".productV10OptionItemValuesLabelInput");
        if (optionInput) {
            updateUnavailableOptions();
            updateProduct().catch(error => console.error(error));
        }
    });

    section.addEventListener("submit", function (e) {
        const form = e.target.closest(".productV10Form");
        if (!form) {
            return;
        }

        e.preventDefault();
        submitForm(form).catch(error => console.error(error));
    });

    /********************* Init *********************/

    // read the freshly rendered markup and wire up everything that depends on the current product
    function initSection() {
        product = JSON.parse(section.querySelector(".productV10JSON").textContent);
        activeVariant = JSON.parse(section.querySelector(".productV10VariantJSON").textContent);
        preOrder = JSON.parse(section.querySelector(".productV10PreOrderJSON").textContent);

        // no product has been picked in the theme editor yet, so there is nothing to wire up
        if (!product || !activeVariant) {
            console.warn(`Product section with ID ${sectionID} has no product selected.`);
            return;
        }

        initMainSwipers();
        initVariantSwipers();

        syncForm();
        syncColourLabel();
        updateUnavailableOptions();
    }

    initSection();

    // a deep linked variant already renders its own gallery, so show that instead of the full one
    const urlVariant = new URL(window.location.href).searchParams.get("variant");
    if (activeVariant && urlVariant && Number(urlVariant) === activeVariant.id && section.querySelector(".productV10VisualSwiper")) {
        section.querySelector(".productV10Visual").classList.remove("mainVisual");
    }
}());